import React, { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { getInitials, avatarColor } from "@/lib/avatar";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import StoryCreator from "@/components/social/StoryCreator";
import StoryViewer from "@/components/social/StoryViewer";

const GOLD_RING = "linear-gradient(135deg,#F3D56D,#D4AF37,#8a6d1f)";
const SILVER_RING = "linear-gradient(135deg,#c9d1da,#8b95a1)";

// The little preview inside a story ring: the photo for image stories, the saved
// poster frame for video stories (so it appears instantly, no video load), else null.
function storyPreview(group) {
  const arr = (group && group.stories) || [];
  const s = arr[arr.length - 1];
  if (!s) return null;
  if (s.type === "image") return s.media || null;
  if (s.type === "video") return s.poster || null;
  return null;
}

function Avatar({ name, avatar, size = 60 }) {
  return (
    <div className="rounded-full grid place-items-center font-semibold overflow-hidden text-[var(--rc-gold)]"
      style={{ width: size, height: size, backgroundColor: avatar ? "var(--rc-bg)" : avatarColor(name) }}>
      {avatar ? <img src={fileUrl(avatar)} alt="" className="h-full w-full object-cover" /> : <span>{getInitials(name)}</span>}
    </div>
  );
}

function Bubble({ ring, onClick, name, avatar, badge, testid, dim, glow }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`shrink-0 flex flex-col items-center gap-1.5 w-[74px] ${dim ? "opacity-60" : ""}`}>
      <div className="relative rounded-full p-[2.5px]" style={{ background: ring || "transparent", boxShadow: glow ? "0 0 0 1px rgba(243,213,109,.5), 0 0 16px 1px rgba(212,175,55,.6)" : "none" }}>
        <div className="rounded-full p-[2px] bg-[var(--rc-bg)]">
          <Avatar name={name} avatar={avatar} />
        </div>
        {badge}
      </div>
      <span className={`text-[11px] truncate max-w-[70px] ${ring === GOLD_RING ? "text-[var(--rc-gold)]" : "text-[var(--rc-silver)]"}`}>{name}</span>
    </button>
  );
}

export default function StoriesBar({ refreshSignal }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState({ me: null, groups: [] });
  const [creator, setCreator] = useState(false);
  const [viewer, setViewer] = useState(null); // { groups, startGroup }

  const load = useCallback(async () => {
    try { const { data } = await api.get("/social/stories/feed"); setData(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load, refreshSignal]);

  // Build the ordered list of groups for the viewer (me first if present, then others)
  const viewerGroups = () => {
    const list = [];
    if (data.me) list.push({ ...data.me, is_me: true });
    data.groups.forEach((g) => list.push(g));
    return list;
  };
  const openViewerAt = (authorId) => {
    const list = viewerGroups();
    const idx = list.findIndex((g) => g.author_id === authorId);
    if (idx >= 0) setViewer({ groups: list, startGroup: idx });
  };

  const firstName = (user.name || "").split(" ")[0];
  const meHasStories = data.me && data.me.stories?.length;

  return (
    <div className="-mx-4 px-4">
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1" data-testid="stories-bar">
        {/* Your Story */}
        <div onClick={() => meHasStories ? openViewerAt(user.id) : setCreator(true)} data-testid="stories-your-story" role="button" tabIndex={0}
          className="shrink-0 flex flex-col items-center gap-1.5 w-[74px] cursor-pointer">
          <div className="relative rounded-full p-[2.5px]" style={{ background: meHasStories ? GOLD_RING : "transparent" }}>
            <div className="rounded-full p-[2px] bg-[var(--rc-bg)]"><Avatar name={user.name} avatar={meHasStories ? (storyPreview(data.me) || user.avatar) : user.avatar} /></div>
            <button onClick={(e) => { e.stopPropagation(); setCreator(true); }} data-testid="stories-add-btn"
              className="absolute -bottom-0.5 -right-0.5 h-[22px] w-[22px] grid place-items-center rounded-full bg-[var(--rc-gold)] text-black border-2 border-[var(--rc-bg)]"><Plus size={14} strokeWidth={3} /></button>
          </div>
          <span className="text-[11px] text-[var(--rc-silver)]">{t("your_story")}</span>
        </div>

        {/* Friends' stories — unseen first, brightest gold + glow */}
        {[...data.groups]
          .sort((a, b) => ((b.has_unviewed ? 1 : 0) - (a.has_unviewed ? 1 : 0))
            || String(b.latest || "").localeCompare(String(a.latest || "")))
          .map((g) => (
          <Bubble key={g.author_id} testid={`story-bubble-${g.author_id}`}
            ring={g.has_unviewed ? GOLD_RING : SILVER_RING} glow={g.has_unviewed}
            name={g.author_name?.split(" ")[0]} avatar={storyPreview(g) || g.author_avatar}
            onClick={() => openViewerAt(g.author_id)} />
        ))}
      </div>

      {creator && <StoryCreator onClose={() => setCreator(false)} onPosted={() => load()} />}
      {viewer && <StoryViewer groups={viewer.groups} startGroup={viewer.startGroup} onClose={() => { setViewer(null); load(); }} onDeleted={() => load()} />}
    </div>
  );
}
